import { ReactNode, useState } from 'react';
import { 
  FileText, 
  Image as ImageIcon, 
  Settings, 
  LogOut, 
  Clock, 
  Menu, 
  X, 
  User, 
  Trash2, 
  Newspaper, 
  Car, 
  Star, 
  FileCheck // <--- Added this import
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useRouter } from './Router';

interface AdminLayoutProps {
  children: ReactNode;
  // Added 'quote-requests' to the allowed types
  currentPage: 'pending' | 'settings' | 'analytics' | 'listings' | 'actualite' | 'banners' | 'car-brands' | 'car-models' | 'featured-models' | 'hero' | 'quote-requests';
}

const NAVIGATION_ITEMS = [
  {
    name: 'Demandes en Attente',
    href: '/admin/dashboard',
    icon: Clock,
    page: 'pending',
    color: 'text-orange-600 bg-orange-50'
  },
  // New Item: Demandes de Devis
  {
    name: 'Demandes de Devis',
    href: '/admin/quote-requests',
    icon: FileCheck,
    page: 'quote-requests',
    color: 'text-blue-600 bg-blue-50'
  },
  {
    name: 'Gestion des Annonces',
    href: '/admin/listings',
    icon: Trash2,
    page: 'listings',
    color: 'text-red-600 bg-red-50'
  },
  {
    name: 'Gestion Articles',
    href: '/admin/actualite',
    icon: Newspaper,
    page: 'actualite',
    color: 'text-green-600 bg-green-50'
  },
  {
    name: 'Gestion des Bannières',
    href: '/admin/banners',
    icon: ImageIcon,
    page: 'banners',
    color: 'text-purple-600 bg-purple-50'
  },
  {
    name: 'Hero Sections',
    href: '/admin/hero',
    icon: ImageIcon,
    page: 'hero',
    color: 'text-indigo-600 bg-indigo-50'
  },
  {
    name: 'Marques de Voitures',
    href: '/admin/car-brands',
    icon: Car,
    page: 'car-brands',
    color: 'text-indigo-600 bg-indigo-50'
  },
  {
    name: 'Modèles de Voitures',
    href: '/admin/car-models',
    icon: FileText,
    page: 'car-models',
    color: 'text-pink-600 bg-pink-50'
  },
  {
    name: 'Modèles Vedettes',
    href: '/admin/featured-models',
    icon: Star,
    page: 'featured-models',
    color: 'text-yellow-600 bg-yellow-50'
  },
  {
    name: 'Paramètres',
    href: '/admin/settings',
    icon: Settings,
    page: 'settings',
    color: 'text-gray-600 bg-gray-50'
  }
];

export function AdminLayout({ children, currentPage }: AdminLayoutProps) {
  const { signOut } = useAuth();
  const { navigate } = useRouter();
  const [loggingOut, setLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    setLoggingOut(true);
    await signOut();
    navigate('/admin/login');
  };

  const currentItem = NAVIGATION_ITEMS.find(item => item.page === currentPage);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Left Section */}
            <div className="flex items-center space-x-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>

              {/* Logo */}
              <div className="flex items-center space-x-2">
                <img
                  src="https://www.argusautomobile.com/logo.png"
                  alt="Argus Admin Logo"
                  className="w-24 h-auto object-contain"
                />
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-3">
              {/* User Menu */}
              <div className="flex items-center space-x-2">
                <div className="hidden sm:block text-right">
                  <p className="text-xs font-medium text-gray-900">Administrateur</p>
                </div>
                <div className="w-7 h-7 bg-gradient-to-br from-gray-300 to-gray-400 rounded-md flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-white" />
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleSignOut}
                disabled={loggingOut}
                className="flex items-center space-x-1 text-gray-700 hover:text-red-600 hover:bg-red-50 px-2 py-1.5 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline text-xs">
                  {loggingOut ? 'Déconnexion...' : 'Déconnexion'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:fixed lg:inset-y-0 lg:pt-14 lg:bg-white lg:border-r lg:border-gray-200">
          <div className="flex-1 flex flex-col min-h-0">
            {/* Current Page Info */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                {currentItem && (
                  <>
                    <div className={`p-1.5 rounded-lg ${currentItem.color}`}>
                      <currentItem.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-medium text-gray-900">{currentItem.name}</h2>
                      <p className="text-xs text-gray-500">Page actuelle</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {NAVIGATION_ITEMS.map((item) => {
                const isActive = item.page === currentPage;
                return (
                  <Link
                    key={item.page}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative overflow-hidden ${
                      isActive
                        ? 'bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#D4452C]'
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-[#D4452C] to-[#b83a24] opacity-90"></div>
                    )}
                    <div className="relative flex items-center space-x-2">
                      <item.icon
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isActive
                            ? 'text-white'
                            : 'text-gray-500 group-hover:text-[#D4452C]'
                        } ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}
                      />
                      <span className="relative text-xs">{item.name}</span>
                    </div>
                    {isActive && (
                      <div className="absolute right-2 w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </Link>
                );
              })}
            </nav>

          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              {/* Close Button */}
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="ml-1 flex items-center justify-center h-8 w-8 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Mobile Navigation */}
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4 mb-6">
                  <img
                    src="https://www.argusautomobile.com/logo.png"
                    alt="Argus Admin Logo"
                    className="w-24 h-auto object-contain"
                  />
                </div>

                <nav className="px-3 space-y-1">
                  {NAVIGATION_ITEMS.map((item) => {
                    const isActive = item.page === currentPage;
                    return (
                      <Link
                        key={item.page}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-[#D4452C]'
                        }`}
                      >
                        <item.icon
                          className={`mr-2 h-4 w-4 ${
                            isActive
                              ? 'text-white'
                              : 'text-gray-500 group-hover:text-[#D4452C]'
                          }`}
                        />
                        <span className="text-xs">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className={`flex-1 ${sidebarOpen ? '' : 'lg:pl-60'} transition-all duration-200 overflow-x-hidden`}>
          <div className="min-h-screen p-3 sm:p-4 lg:p-6">
            <div className="max-w-7xl mx-auto w-full overflow-x-hidden">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}